import { AbstractStore }             from 'modelx-jts-common';
import { ApiCollection }             from 'modelx-jts-common';
import { PagedCollection }           from 'modelx-jts-common';
import ProjectPoleBudgetModel        from 'models/budgetModels/ProjectPoleBudgetModel';
import ProjectCategoryBudgetModel    from 'models/budgetModels/ProjectCategoryBudgetModel';
import ProjectSubCategoryBudgetModel from 'models/budgetModels/ProjectSubCategoryBudgetModel';
import AccountLsdModel               from '../models/AccountLsdModel';
import { ROLE_LSD_BUDGET_MANAGER }   from 'constants/Roles';
import { action }                    from 'mobx';
import { computed }                  from 'mobx';
import { observable }                from 'mobx';
import { toJS }                      from 'mobx';
import locationTools                 from '../tools/locationTools';
import appStore                      from './AppStore';
import ProjectPoleStatsModel         from '../models/ProjectPoleStatsModel';
import ProjectCategoryStatsModel     from '../models/ProjectCategoryStatsModel';
import ExpenseBudgetModel            from '../models/ExpenseBudgetModel';
import ProjectSubCategoryStatsModel  from '../models/ProjectSubCategoryStatsModel';
import ProjectStatsModel             from '../models/ProjectStatsModel';
import ExpenseSupplierContactModel   from '../models/ExpenseSupplierContactModel';
import ProjectPoleModel              from '../models/ProjectPoleModel';
import UserApiCollection             from '../models/collections/UserApiCollection';
import ProjectCategoryModel          from '../models/ProjectCategoryModel';
import { getUniqueDefinedValues }    from 'tools/jsTools';
import ProjectBudgetModel            from 'models/budgetModels/ProjectBudgetModel';

const defaultProjectPoleColor = '#4eadaa';

export class OrganigramStore extends AbstractStore {

	@observable public budgetManagerCollection = new ApiCollection(AccountLsdModel);
	@observable public expenseBudgetCollection = new PagedCollection(ExpenseBudgetModel);
	@observable public expenseSupplierContactCollection = new ApiCollection(ExpenseSupplierContactModel);
	@observable public piloteCollection = new ApiCollection(AccountLsdModel);
	@observable public projectCategoryBudgetCollection = new ApiCollection(ProjectCategoryBudgetModel);
	@observable public projectCategoryStats = new ProjectCategoryStatsModel();
	@observable public projectBudgetCollection = new ApiCollection(ProjectBudgetModel);
	@observable public projectPoleBudgetCollection = new ApiCollection(ProjectPoleBudgetModel);
	@observable public projectPoleStats = new ProjectPoleStatsModel();
	@observable public projectStats = new ProjectStatsModel();
	@observable public projectSubCategoryBudgetCollection = new ApiCollection(ProjectSubCategoryBudgetModel);
	@observable public projectSubCategoryStats = new ProjectSubCategoryStatsModel();
	@observable public userCollection =  new UserApiCollection();

	@observable public selectedPole = null;
	@observable public selectedCategory = null;
	@observable public selectedSubCategory = null;
	@observable public selectedProject = null;

	@observable public virtualActivatedPoleBudgetCollection = this.projectPoleBudgetCollection.createVirtualCollection(
		(m: ProjectPoleBudgetModel) => m.activated);

	@observable public virtualActivatedCategoryBudgetCollection = this.projectCategoryBudgetCollection.createVirtualCollection(
		(m: ProjectCategoryBudgetModel) => m.activated);

	@observable public virtualActivatedSubCategoryBudgetCollection = this.projectSubCategoryBudgetCollection.createVirtualCollection(
		(m: ProjectSubCategoryBudgetModel) => m.activated);

	public dirty = false;

	@observable
	public state;

	public historyReplace;

	public historyRemove;

	private _expenseGroupId;

	private _lastCallToFetchUsers = null;

	constructor() {
		super();

		this.projectPoleBudgetCollection.addSort('position', true);
		this.projectCategoryBudgetCollection.addSort('position', true);
		this.projectSubCategoryBudgetCollection.addSort('position', true);
		this.projectBudgetCollection.addSort('title', true);
		this.expenseBudgetCollection.addSort('createdAt', false);
		this.expenseBudgetCollection.setLimit(50);

		this.historyReplace = parameter => {
			window.history.replaceState(
				null,
				null,
				locationTools.changeValues(
					window.location.href,
					parameter
				));
		};

		this.historyRemove = parameter => {
			if (typeof parameter === 'object') {
				window.history.replaceState(
					null,
					null,
					locationTools.removeKeys(
						window.location.href,
						parameter
					));
			} else {
				window.history.replaceState(
					null,
					null,
					locationTools.removeKey(
						window.location.href,
						parameter
					));
			}
		};

		this.setState({
			activeTab: locationTools.getValue(window.location.href, 'tab') || 'dashboard',

			activationMode: false,

			projectPoleSelectedColor: defaultProjectPoleColor,

			projectCategorySelected: null,
			projectPoleSelected: null,
			projectSelected: null,
			projectSubCategorySelected: null,

			responsiblesMode: false,

			recoverMode: false
		});
	}

	@action
	public setExpenseGroupId = async (id: number | string, force = false): Promise<void> => {
		if (
			(
				appStore.currentCompanyId
				&& (!!id && this._expenseGroupId + '' !== id + '')
			)
			|| force
		) {
			this.clearStore();
			this._expenseGroupId = id;
			await this.fetchProjectPoleCollection(id);
		}
	}

	/**
	 * Deprecated because can not return a promise
	 * @deprecated
	 *
	 * @param id
	 */
	public set expenseGroupId(id: number) {
		if (!!id && this._expenseGroupId + '' !== id + '') {
			this.clearStore();
			this.fetchProjectPoleCollection(id);
			this._expenseGroupId = id;
		}
	}

	public get expenseGroupId() {
		return this._expenseGroupId;
	}

	public clearStore = () => {
		this.projectPoleBudgetCollection.clear();
		this.projectCategoryBudgetCollection.clear();
		this.projectSubCategoryBudgetCollection.clear();
		this.projectBudgetCollection.clear();
		this.expenseBudgetCollection.clear();
		this.expenseSupplierContactCollection.clear();

		this.setState({
			projectPoleSelected: null,
			projectCategorySelected: null,
			projectSubCategorySelected: null,
			projectSelected: null
		});
	}

	public fetchBudgetManagerCollection = async (): Promise<void> => {
		const companyId = appStore.currentCompanyId;
		await this.budgetManagerCollection
			.addSort('userSync.firstName', true)
			.addSort('userSync.lastName', true)
			.setFilter('userGroupCompanies.company', companyId)
			.setFilter('isGranted', ROLE_LSD_BUDGET_MANAGER)
			.list();
	}

	public fetchPiloteCollection = async (): Promise<void> => {
		const companyId = appStore.currentCompanyId;
		await this.piloteCollection
			.addSort('userSync.firstName', true)
			.addSort('userSync.lastName', true)
			.setFilter('userGroupCompanies.company', companyId)
			.list();
	}

	public fetchProjectPoleCollection = async (expenseGroupId: number | string = this.expenseGroupId): Promise<void> => {
		const companyId = appStore.currentCompanyId;

		await Promise.all([
			this.projectPoleStats
				.set('expenseGroup', expenseGroupId)
				.fetch(),

			this.projectPoleBudgetCollection
				.setFilter('company', companyId)
				.setFilter('budgetExpenseGroup', expenseGroupId)
				.list(),
		]);

		this.projectPoleBudgetCollection.forEach((projectPole: ProjectPoleModel) => {
			const activated = projectPole.activatedExpenseGroups.find(
				activatedExpenseGroup => {
					const js = toJS(activatedExpenseGroup);
					return js.id + '' === expenseGroupId + '';
				}
			);

			projectPole.set('activated', !!activated);
		});

		await this.fetchUsers();
	}

	public fetchProjectCategoryCollectionAndSetColor = async (
		projectPoleId,
		projectPoleColor = defaultProjectPoleColor,
		expenseGroupId = this.expenseGroupId
	): Promise<void> => {
		await this.fetchProjectCategoryCollection(projectPoleId, expenseGroupId);

		this.setState({
			projectPoleSelectedColor: projectPoleColor,
		});
	}

	public fetchProjectCategoryStats = async (
		projectPoleId = this.state.projectPoleSelected,
		expenseGroupId = this.expenseGroupId
	): Promise<void> => {
		this.projectCategoryStats.set({
			projectPole: projectPoleId,
			expenseGroup: expenseGroupId
		});
		await this.projectCategoryStats.fetch();
	}

	public fetchProjectSubCategoryStats = async (
		projectCategoryId = this.state.projectCategorySelected,
		expenseGroupId = this.expenseGroupId
	): Promise<void> => {
		this.projectSubCategoryStats.set({
			projectCategory: projectCategoryId,
			expenseGroup: expenseGroupId
		});
		await this.projectSubCategoryStats.fetch();
	}

	public fetchProjectCategoryCollection = async (
		projectPoleId,
		expenseGroupId = this.expenseGroupId,
	): Promise<void> => {
		if (!this.state.recoverMode) {
			this.historyReplace([
				{
					key: 'projectPole',
					value: projectPoleId,
				},
			]);

			this.historyRemove(['projectCategory', 'projectSubCategory', 'project']);
		}

		await Promise.all([
			this.fetchProjectCategoryStats(projectPoleId, expenseGroupId),

			this.projectCategoryBudgetCollection
				.setFilter('projectPole', projectPoleId)
				.setFilter('budgetExpenseGroup', expenseGroupId)
				.list(),
		]);

		if (this.state.recoverMode) {
			this.setState({
				projectPoleSelected: projectPoleId,
			});
		} else if (projectPoleId !== this.state.projectPoleSelected) {
			this.projectSubCategoryBudgetCollection.clear();
			this.projectBudgetCollection.clear();
			this.expenseBudgetCollection.clear();
			this.expenseSupplierContactCollection.clear();

			this.setState({
				projectPoleSelected: projectPoleId,
				projectCategorySelected: null,
				projectSubCategorySelected: null,
				projectSelected: null,
			});
		}

		this.projectCategoryBudgetCollection.forEach((projectCategory: ProjectCategoryModel) => {
			const activated = projectCategory.activatedExpenseGroups.find(
				activatedExpenseGroup => {
					const js = toJS(activatedExpenseGroup);
					return js.id + '' === expenseGroupId + '';
				}
			);

			projectCategory.set('activated', !!activated);
		});
	}

	public fetchProjectSubCategoryCollection = async (
		projectCategoryId,
		expenseGroupId = this.expenseGroupId,
	): Promise<void> => {
		if (!this.state.recoverMode) {
			this.historyReplace([
				{
					key: 'projectPole',
					value: this.state.projectPoleSelected,
				},
				{
					key: 'projectCategory',
					value: projectCategoryId,
				},
			]);

			this.historyRemove(['projectSubCategory', 'project']);
		}

		const promises = [];

		if (!!this.state.projectPoleSelected) {
			promises.push(
				this.fetchProjectCategoryStats(this.state.projectPoleSelected, expenseGroupId),
			);
		}

		promises.push(
			this.fetchProjectSubCategoryStats(projectCategoryId, expenseGroupId),

			this.projectSubCategoryBudgetCollection
				.setFilters({
					projectCategory: projectCategoryId,
					budgetExpenseGroup: expenseGroupId,
				})
				.list(),
		);

		await Promise.all(promises);

		if (this.state.recoverMode) {
			this.setState({
				projectCategorySelected: projectCategoryId,
			});
		} else if (projectCategoryId !== this.state.projectCategorySelected) {
			this.projectBudgetCollection.clear();
			this.expenseBudgetCollection.clear();
			this.expenseSupplierContactCollection.clear();

			this.setState({
				projectCategorySelected: projectCategoryId,
				projectSubCategorySelected: null,
				projectSelected: null,
			});
		}

		this.projectSubCategoryBudgetCollection.forEach(projectSubCategory => {
			const activated = projectSubCategory.get('activatedExpenseGroups').find(
				activatedExpenseGroup => {
					const js = toJS(activatedExpenseGroup);
					return js.id + '' === expenseGroupId + '';
				},
			);

			projectSubCategory.set('activated', !!activated);
		});
	}

		public fetchProjectCollection = async (
		projectSubCategoryId,
		expenseGroupId = this.expenseGroupId
	): Promise<void> => {
		if (!this.state.recoverMode) {
			this.historyReplace([
				{
					key: 'projectPole',
					value: this.state.projectPoleSelected,
				},
				{
					key: 'projectCategory',
					value: this.state.projectCategorySelected,
				},
				{
					key: 'projectSubCategory',
					value: projectSubCategoryId,
				},
			]);

			this.historyRemove(['project']);
		}

		await Promise.all([
			this.fetchProjectStats(projectSubCategoryId, expenseGroupId),

			this.projectBudgetCollection
				.setFilters({
					projectSubCategory: projectSubCategoryId,
					expenseGroup: expenseGroupId,
				})
				.list(),
		]);

		if (this.state.recoverMode) {
			this.setState({
				projectSubCategorySelected: projectSubCategoryId,
			});
		} else if (projectSubCategoryId !== this.state.projectSubCategorySelected) {
			this.expenseBudgetCollection.clear();
			this.expenseSupplierContactCollection.clear();

			this.setState({
				projectSubCategorySelected: projectSubCategoryId,
				projectSelected: null,
			});
		}
	}

	public fetchProjectStats = async (
		projectSubCategoryId = this.state.projectSubCategorySelected,
		expenseGroupId = this.expenseGroupId
	): Promise<void> => {
		this.projectStats.set({
			projectSubCategory: projectSubCategoryId,
			expenseGroup: expenseGroupId
		});
		await this.projectStats.fetch();
	}

	public fetchUsers = async (): Promise<void> => {
		const call = async () => {
			this.userCollection.clear();

			const usersIds = [];

			this.projectPoleBudgetCollection.forEach((projectPole: ProjectPoleModel) => {
				usersIds.push(...projectPole.responsiblesIds);
			});

			this.expenseBudgetCollection.forEach((expense: ExpenseBudgetModel) => {
				usersIds.push(
					...expense.currentResponsiblesIds,
					expense.createdById,
				);
			});

			const uniqueDefinedUsersIds = getUniqueDefinedValues(usersIds);

			if (!uniqueDefinedUsersIds.length) {
				return;
			}

			await this.userCollection
				.setFilter('id', uniqueDefinedUsersIds)
				.list();
		};
		
		if (this._lastCallToFetchUsers) {
			clearTimeout(this._lastCallToFetchUsers);
		}

		// this is done to avoid multiple calls at the same time of fetchUsers
		this._lastCallToFetchUsers = setTimeout(
			call,
			500,
		);
	}

	public fetchExpenseCollection = async (projectId): Promise<void> => {
		if (!this.state.recoverMode) {
			this.historyReplace([
				{ key: 'projectPole', value: this.state.projectPoleSelected },
				{ key: 'projectCategory', value: this.state.projectCategorySelected },
				{ key: 'projectSubCategory', value: this.state.projectSubCategorySelected },
				{ key: 'project', value: projectId },
			]);
		}

		// reset page to first page for expenses box
		await this.expenseBudgetCollection
			.setPage(1)
			.setFilter('project', projectId)
			.list();

		const promises: Promise<any>[] = [
			this.fetchUsers(),
		];

		const expensesIds = getUniqueDefinedValues(this.expenseBudgetCollection.map(e => e.id));

		if (expensesIds.length) {
			promises.push(
				this.expenseSupplierContactCollection
					.setFilter('expense', expensesIds)
					.list(),
			);
		}

		await Promise.all(promises);

		this.setState({
			projectSelected: projectId,
		});
	}

	@action
	public setState = (newState) => {
		this.state = {
			...this.state,
			...newState,
		};
	}

	@computed
	public get currentProjectPole(): ProjectPoleBudgetModel | null {
		return this.projectPoleBudgetCollection.find(
			projectPole => projectPole.id + '' === this.state.projectPoleSelected + ''
		) as ProjectPoleBudgetModel;
	}

	@computed
	public get currentProjectCategory(): ProjectCategoryBudgetModel | null {
		return this.projectCategoryBudgetCollection.find(
			projectCategory => projectCategory.id + '' === this.state.projectCategorySelected + ''
		) as ProjectCategoryBudgetModel;
	}

	@computed
	public get currentProjectSubCategory(): ProjectSubCategoryBudgetModel | null {
		return this.projectSubCategoryBudgetCollection.find(
			projectSubCategory => projectSubCategory.id + '' === this.state.projectSubCategorySelected + ''
		) as ProjectSubCategoryBudgetModel;
	}

	@computed
	public get currentProject(): ProjectBudgetModel | null {
		return this.projectBudgetCollection.find(
			project => project.id + '' === this.state.projectSelected + ''
		) as ProjectBudgetModel;
	}

	// @action
	// public set pole(projectPole: ProjectPoleBudgetModel) {
	// 	this.setState({
	// 		projectPoleSelected: projectPole.id,
	// 	});
	//
	// 	this.selectedPole = projectPole;
	// }
	//
	// @action
	// public set category(projectCategory: ProjectCategoryBudgetModel) {
	// 	this.setState({
	// 		projectCategorySelected: projectCategory.id,
	// 	});
	//
	// 	this.selectedCategory = projectCategory;
	// }
	//
	// @action
	// public set subCategory(projectSubCategory: ProjectSubCategoryBudgetModel) {
	// 	this.setState({
	// 		projectSubCategorySelected: projectSubCategory.id,
	// 	});
	//
	// 	this.selectedSubCategory = projectSubCategory;
	// }
	//
	// @action
	// public set project(project: ProjectPoleBudgetModel) {
	// 	this.setState({
	// 		projectSelected: project.id,
	// 	});
	//
	// 	this.selectedProject = project;
	// }
}

export default new OrganigramStore();
